import { useContext } from "react";

import {
  ConfigProvider,
  Descriptions,
  Tag,
  Button,
  Flex,
  Typography,
  Checkbox,
} from "antd";

import MainContext from "@/layouts/MainLayout/context";

import EmployeeProfile from "@/components/cards/EmployeeProfile";

import PlusIcon from "@/icons/PlusIcon";
import ExclamationCircleIcon from "@/icons/ExclamationCircleIcon";

import { formateISODateToMoment, formateDate } from "@/utils";

import {
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  TASK_TYPE_OPTIONS,
} from "@/constants/tasks";

const InfoSection = ({
  data,
  status,
  priority,
  taskType,
  is_overdue,
  isUserCanEditExecutor,
  isUserCanEditStartDate,
  isUserCanEditDueDate,
  isUserCanEditCoexecutors,
  isUserCanEditAcceptor,
  isUserCanEditSprint,
  handleEditTask,
  setIsGroupTask,
  showSetMeExecutor,
  showSetMeAcceptor,
  setMeExecutor,
  setMeAcceptor,
}) => {
  const { config } = useContext(MainContext);
  const sprintName = config?.entities?.sprint?.name?.singular || "Спринт";

  const getPriorityIcon = (color) => {
    return PRIORITY_OPTIONS.find((item) => item.value === color)?.icon;
  };
  const getStatusIcon = (color) => {
    return STATUS_OPTIONS.find((item) => item.value === color)?.icon;
  };
  const getTaskTypeIcon = (color) => {
    return TASK_TYPE_OPTIONS.find((item) => item.value === color)?.icon;
  };

  const handleGroupTaskChange = async (e) => {
    await setIsGroupTask(e.target.checked);
  };

  return (
    <Flex vertical gap={15}>
      <ConfigProvider
        theme={{
          components: {
            Descriptions: {
              itemPaddingBottom: 10,
            },
          },
        }}
      >
        <Descriptions
          column={1}
          colon={false}
          styles={{
            label: {
              width: 130,
            },
          }}
        >
          <Descriptions.Item label="Тип задачи:">
            {taskType && (
              <Tag
                icon={getTaskTypeIcon(taskType.key)}
                className="tag-with-icon lg"
              >
                {taskType.name}
              </Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Автор:">
            <EmployeeProfile
              {...data.author}
              fullName={false}
              avatarSize={24}
            />
          </Descriptions.Item>

          <Descriptions.Item label="Исполнитель:">
            <Flex vertical gap={5} align="flex-start">
              {!!data.executor ? (
                <EmployeeProfile
                  {...data.executor}
                  fullName={false}
                  avatarSize={24}
                />
              ) : isUserCanEditExecutor ? (
                <Button
                  color="default"
                  variant="link"
                  size="small"
                  type="text"
                  icon={<PlusIcon />}
                  onClick={handleEditTask}
                  style={{ paddingInline: 0 }}
                >
                  Назначить
                </Button>
              ) : (
                "-"
              )}

              {showSetMeExecutor && (
                <Typography.Link
                  onClick={setMeExecutor}
                  style={{ fontSize: "12px" }}
                >
                  Назначить на меня
                </Typography.Link>
              )}
            </Flex>
          </Descriptions.Item>

          <Descriptions.Item label="Приемщик:">
            <Flex vertical gap={5} align="flex-start">
              {!!data.assignee_acceptor ? (
                <EmployeeProfile
                  {...data.assignee_acceptor}
                  fullName={false}
                  avatarSize={24}
                />
              ) : isUserCanEditAcceptor ? (
                <Button
                  color="default"
                  variant="link"
                  size="small"
                  type="text"
                  icon={<PlusIcon />}
                  onClick={handleEditTask}
                  style={{ paddingInline: 0 }}
                >
                  Назначить
                </Button>
              ) : (
                "-"
              )}

              {showSetMeAcceptor && (
                <Typography.Link
                  onClick={setMeAcceptor}
                  style={{ fontSize: "12px" }}
                >
                  Назначить на меня
                </Typography.Link>
              )}
            </Flex>
          </Descriptions.Item>

          <Descriptions.Item label="Статус:">
            {status && (
              <Tag
                icon={getStatusIcon(status.color)}
                color={status.color}
                className="tag-with-icon-color"
              >
                {status.name}
              </Tag>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Приоритет:">
            {priority && (
              <Tag
                icon={getPriorityIcon(priority.color)}
                className="tag-with-icon"
              >
                {priority.name}
              </Tag>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Дата начала:">
            {!!data.start_date ? (
              formateDate(data.start_date)
            ) : isUserCanEditStartDate ? (
              <Button
                color="default"
                variant="link"
                size="small"
                type="text"
                icon={<PlusIcon />}
                onClick={handleEditTask}
                style={{ paddingInline: 0 }}
              >
                Назначить
              </Button>
            ) : (
              "-"
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Срок исполнения:">
            {!!data.due_date ? (
              <Flex align="center" gap={4}>
                {is_overdue && (
                  <ExclamationCircleIcon
                    style={{ fontSize: 14, color: "#E66A25" }}
                  />
                )}
                <span>{formateISODateToMoment(data.due_date)}</span>
              </Flex>
            ) : isUserCanEditDueDate ? (
              <Button
                color="default"
                variant="link"
                size="small"
                type="text"
                icon={<PlusIcon />}
                onClick={handleEditTask}
                style={{ paddingInline: 0 }}
              >
                Назначить
              </Button>
            ) : (
              "-"
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Рабочая группа:">
            {data.unit?.name || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="Соисполнители:">
            {!!data.coexecutors?.length ? (
              <Flex vertical gap={5}>
                {data.coexecutors.map((executor) => (
                  <EmployeeProfile
                    {...executor}
                    key={executor.uuid}
                    fullName={false}
                    avatarSize={24}
                  />
                ))}
              </Flex>
            ) : isUserCanEditCoexecutors ? (
              <Button
                color="default"
                variant="link"
                size="small"
                type="text"
                icon={<PlusIcon />}
                onClick={handleEditTask}
                style={{ paddingInline: 0 }}
              >
                Назначить
              </Button>
            ) : (
              "-"
            )}
          </Descriptions.Item>

          <Descriptions.Item label={sprintName + ":"}>
            {data.sprint ? (
              data.sprint.name
            ) : isUserCanEditSprint ? (
              <Button
                color="default"
                variant="link"
                size="small"
                type="text"
                icon={<PlusIcon />}
                onClick={handleEditTask}
                style={{ paddingInline: 0 }}
              >
                Назначить
              </Button>
            ) : (
              "-"
            )}
          </Descriptions.Item>
        </Descriptions>
      </ConfigProvider>

      <Checkbox
        disabled={data.readOnly}
        checked={data.is_accessible_for_all_in_unit}
        onChange={handleGroupTaskChange}
      >
        Уведомлять всех в рабочей группе
      </Checkbox>
    </Flex>
  );
};

export default InfoSection;
